import { useQueryClient } from "@tanstack/react-query";
import { Fragment, lazy, Suspense, useEffect, useState } from "react";
import type { LayoutDoc } from "../../shared/layout";
import type { HouseholdSettings, Member } from "../../shared/models";
import { SEASONAL_THEMES, seasonDates } from "../../shared/themes";
import type { SettingsSection } from "../app/uiContext";
import { setDevicePref, useDevicePrefs } from "../app/devicePrefs";
import { useHousehold } from "../app/useHousehold";
import { api } from "../data/api";
import { clearSnapshots } from "../data/persist";
import { useAiStatus, useCalendarChoices, useCalendarSetup, useCalendarStatus, useDiagnostics, useMe, useMembers, useUpdateSettings, useWeather } from "../data/queries";
import { Avatar } from "../ui/Avatar";
import { Icon, type IconName } from "../ui/Icon";
import { Confirm, Sheet } from "../ui/Sheet";
import { relativeAge } from "../ui/time";
import { ScreensaverSettings } from "../widgets/photos/Screensaver";
import { SleepSettings } from "../app/Sleep";
import { MemberSignIn } from "./MemberSignIn";

const LocationMap = lazy(() => import("./LocationMap").then((m) => ({ default: m.LocationMap })));

const SECTIONS: { key: SettingsSection; label: string; icon: IconName; admin?: boolean }[] = [
  { key: "household", label: "Household", icon: "home" },
  { key: "location", label: "Weather location", icon: "pin" },
  { key: "display", label: "Display", icon: "sun" },
  { key: "members", label: "People", icon: "user" },
  { key: "calendar", label: "Google Calendar", icon: "calendar" },
  { key: "ai", label: "AI features", icon: "sparkle" },
  { key: "diagnostics", label: "Diagnostics", icon: "info", admin: true },
];

export function SettingsPanel({ initial, onClose, layouts }: { initial: SettingsSection; onClose: () => void; layouts: LayoutDoc[] }) {
  const [section, setSection] = useState<SettingsSection>(initial);
  const { isAdmin } = useHousehold();
  const visible = SECTIONS.filter((s) => !s.admin || isAdmin);
  return (
    <Sheet title="Settings" onClose={onClose} size="lg">
      <div className="settings">
        <nav className="settings-nav" aria-label="Settings sections">
          {visible.map((s) => (
            <button key={s.key} className={section === s.key ? "on" : ""} onClick={() => setSection(s.key)}>
              <Icon name={s.icon} size={20} />
              {s.label}
            </button>
          ))}
        </nav>
        <div className="settings-body">
          <a className="admin-link" href="/admin">
            <Icon name="gear" size="1.1rem" /> Open the full admin page (chores, routines, screens, kitchen…)
          </a>
          {!isAdmin && section !== "household" && <p className="banner">Only adults in the household can change these settings.</p>}
          {section === "household" && <HouseholdSection />}
          {section === "location" && <LocationSection />}
          {section === "display" && <DisplaySection layouts={layouts} />}
          {section === "members" && <MembersSection />}
          {section === "calendar" && <CalendarSection />}
          {section === "ai" && <AiSection />}
          {section === "diagnostics" && <DiagnosticsSection />}
        </div>
      </div>
    </Sheet>
  );
}

function useSave() {
  const update = useUpdateSettings();
  const [error, setError] = useState<string | null>(null);
  const save = (patch: Partial<HouseholdSettings>, onDone?: () => void) => update.mutate(patch, { onSuccess: () => (setError(null), onDone?.()), onError: (e) => setError(e.message) });
  return { save, error, pending: update.isPending };
}

export function HouseholdSection() {
  const me = useMe();
  const qc = useQueryClient();
  const { isAdmin } = useHousehold();
  const hh = me.data?.household;
  const [name, setName] = useState(hh?.name ?? "");
  const [tz, setTz] = useState(hh?.timezone ?? "America/Denver");
  const [msg, setMsg] = useState<string | null>(null);
  const zones = Intl.supportedValuesOf("timeZone").filter((z) => z.startsWith("America/") || z.startsWith("Pacific/Honolulu") || z === "UTC");
  const save = async () => {
    try {
      await api("/household", { method: "PATCH", body: { name, timezone: tz } });
      await qc.invalidateQueries({ queryKey: ["me"] });
      setMsg("Saved");
    } catch (e) {
      setMsg((e as Error).message);
    }
  };
  const signOut = async () => {
    await api("/auth/logout", { method: "POST", body: {} }).catch(() => undefined);
    clearSnapshots();
    window.location.href = "/";
  };
  return (
    <div className="form">
      <h3>Household</h3>
      <label className="field">
        <span>Household name</span>
        <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} disabled={!isAdmin} />
      </label>
      <label className="field">
        <span>Time zone</span>
        <select className="input" value={tz} onChange={(e) => setTz(e.target.value)} disabled={!isAdmin}>
          {zones.map((z) => (
            <option key={z} value={z}>
              {z.replace(/_/g, " ")}
            </option>
          ))}
        </select>
      </label>
      {isAdmin && (
        <div className="row">
          <button className="btn btn-primary" onClick={save}>
            Save
          </button>
          {msg && <span className="muted">{msg}</span>}
        </div>
      )}
      <hr />
      <p className="muted">
        Signed in as <strong>{me.data?.member.displayName}</strong>
        {me.data?.member.email && ` (${me.data.member.email})`}
        {me.data?.devBypass && " — local development mode"}
      </p>
      {!me.data?.devBypass && (
        <button className="btn" onClick={signOut}>
          <Icon name="signout" size={18} /> Sign out
        </button>
      )}
    </div>
  );
}

export function LocationSection() {
  const { settings, isAdmin } = useHousehold();
  const { save, error, pending } = useSave();
  const weather = useWeather(Boolean(settings.location));
  const [pick, setPick] = useState<{ lat: number; lon: number; label: string } | null>(null);
  const [lookupMsg, setLookupMsg] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  const shown = pick ?? settings.location ?? { lat: 39.5, lon: -98.35, label: "" };
  const zoom = pick || settings.location ? 9 : 4;

  const describe = async (lat: number, lon: number) => {
    setPick({ lat, lon, label: "" });
    setLookupMsg("Checking National Weather Service coverage…");
    try {
      const r = await api<{ label: string }>(`/weather/lookup?lat=${lat.toFixed(3)}&lon=${lon.toFixed(3)}`);
      setPick({ lat, lon, label: r.label });
      setLookupMsg(null);
    } catch (e) {
      setLookupMsg((e as Error).message);
    }
  };

  const useDevice = () => {
    if (!navigator.geolocation) return setLookupMsg("This device can’t share its location.");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        // Round to ~1 km: a general area is all weather needs.
        void describe(Math.round(pos.coords.latitude * 100) / 100, Math.round(pos.coords.longitude * 100) / 100);
      },
      (err) => {
        setLocating(false);
        setLookupMsg(err.code === err.PERMISSION_DENIED ? "Location permission was declined. Tap the map instead." : "Couldn’t get this device’s location. Tap the map instead.");
      },
      { enableHighAccuracy: false, timeout: 15_000, maximumAge: 600_000 },
    );
  };

  // With no location yet, suggest this device's area straight away (the browser asks permission first).
  const [suggested, setSuggested] = useState(false);
  useEffect(() => {
    if (!settings.location && isAdmin && !suggested) {
      setSuggested(true);
      useDevice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const candidates = weather.data?.stationCandidates ?? [];
  const selectedStation = weather.data?.observation?.station;

  return (
    <div className="form">
      <h3>Weather location</h3>
      <p className="muted">
        Weather uses a general location (rounded to about a kilometer). Kinmantle picks the nearest official station that has reported a recent, valid reading.
      </p>
      <p>
        <strong>Current:</strong> {settings.location ? `${settings.location.label || "Custom location"} (${settings.location.lat}, ${settings.location.lon})` : "Not set"}
      </p>
      {isAdmin && (
        <>
          <div className="row">
            <button className="btn" onClick={useDevice} disabled={locating}>
              <Icon name="locate" size={18} /> {locating ? "Locating…" : "Use this device’s location"}
            </button>
            <span className="muted small">or tap the map</span>
          </div>
          <Suspense fallback={<div className="location-map" />}>
            <LocationMap lat={shown.lat} lon={shown.lon} zoom={zoom} onPick={(lat, lon) => void describe(Math.round(lat * 100) / 100, Math.round(lon * 100) / 100)} />
          </Suspense>
          {lookupMsg && <p className="muted">{lookupMsg}</p>}
          {pick && pick.label && (
            <div className="row">
              <span>
                Use <strong>{pick.label}</strong>?
              </span>
              <button className="btn btn-primary" disabled={pending} onClick={() => save({ location: pick, pinnedStationId: null }, () => setPick(null))}>
                Save location
              </button>
            </div>
          )}
        </>
      )}

      {settings.location && (
        <>
          <hr />
          <h4>Observation station</h4>
          {candidates.length === 0 ? (
            <p className="muted">Station details appear after the first weather update.</p>
          ) : (
            <ul className="station-list">
              {candidates.map((c) => (
                <li key={c.id} className={c.id === selectedStation?.id ? "selected" : ""}>
                  <span>
                    <strong>{c.id}</strong> {c.name} · {c.distanceKm} km
                  </span>
                  <span className="muted small">{c.id === selectedStation?.id ? "In use" : (c.rejected ?? "")}</span>
                  {isAdmin && (
                    <button className="btn btn-sm" onClick={() => save({ pinnedStationId: settings.pinnedStationId === c.id ? null : c.id })}>
                      {settings.pinnedStationId === c.id ? "Unpin" : "Prefer"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {settings.pinnedStationId && <p className="muted small">Preferred station {settings.pinnedStationId} is tried first, but skipped if its reading isn’t fresh.</p>}
          {isAdmin && (
            <label className="field">
              <span>
                Treat observations as current for up to <strong>{settings.observationMaxAgeMinutes} minutes</strong>
              </span>
              <input
                type="range"
                min={30}
                max={180}
                step={10}
                defaultValue={settings.observationMaxAgeMinutes}
                onPointerUp={(e) => save({ observationMaxAgeMinutes: Number((e.target as HTMLInputElement).value) })}
                onKeyUp={(e) => save({ observationMaxAgeMinutes: Number((e.target as HTMLInputElement).value) })}
              />
            </label>
          )}
        </>
      )}
      {error && <p className="error-text">{error}</p>}
    </div>
  );
}

export function DisplaySection({ layouts }: { layouts: LayoutDoc[] }) {
  const { settings, isAdmin } = useHousehold();
  const device = useDevicePrefs();
  const { save, error } = useSave();
  const opt = <K extends keyof HouseholdSettings>(key: K, value: HouseholdSettings[K], label: string) => (
    <button className={`chip ${settings[key] === value ? "on" : ""}`} disabled={!isAdmin} onClick={() => save({ [key]: value } as Partial<HouseholdSettings>)}>
      {label}
    </button>
  );
  return (
    <div className="form">
      <h3>Display</h3>
      <div className="field">
        <span>Theme</span>
        <div className="chip-row">
          {opt("theme", "dark", "Dark")}
          {opt("theme", "light", "Light")}
          {opt("theme", "auto", "Follow the sun")}
        </div>
        <p className="muted small">“Follow the sun” switches to light at sunrise and dark at sunset for your location.</p>
      </div>
      <div className="field">
        <span>Seasonal theme</span>
        <div className="chip-row">
          {opt("seasonalTheme", "auto", "Automatic by season")}
          {SEASONAL_THEMES.map((t) => (
            <Fragment key={t.id}>{opt("seasonalTheme", t.id, t.name)}</Fragment>
          ))}
          {opt("seasonalTheme", "none", "None")}
        </div>
        <p className="muted small">
          Decorates the dark and light themes for a holiday. Automatic turns each one on for its season:{" "}
          {SEASONAL_THEMES.map((t) => `${t.name} ${seasonDates(t.window)}`).join(", ")}.
        </p>
      </div>
      <label className="field field-toggle">
        <span>
          Reduce visual effects on this screen
          <small className="muted">Turns off background blur. Try this on a Raspberry Pi if scrolling or radar feels slow.</small>
        </span>
        <input type="checkbox" className="switch" checked={device.reduceEffects} onChange={(e) => setDevicePref("reduceEffects", e.target.checked)} />
      </label>
      <div className="field">
        <span>Widgets on this screen</span>
        <div className="chip-row">
          {(
            [
              ["comfortable", "Comfortable"],
              ["compact", "Compact"],
            ] as const
          ).map(([value, label]) => (
            <button key={value} className={`chip ${device.density === value ? "on" : ""}`} onClick={() => setDevicePref("density", value)}>
              {label}
            </button>
          ))}
        </div>
        <p className="muted small">Compact uses less padding and slightly smaller text, so more fits in each widget.</p>
      </div>
      <ScreensaverSettings />
      <SleepSettings />
      <div className="field">
        <span>On-screen keyboard on this screen</span>
        <div className="chip-row">
          {(
            [
              ["auto", "When tapped"],
              ["on", "Always"],
              ["off", "Off"],
            ] as const
          ).map(([value, label]) => (
            <button key={value} className={`chip ${device.touchKeyboard === value ? "on" : ""}`} onClick={() => setDevicePref("touchKeyboard", value)}>
              {label}
            </button>
          ))}
        </div>
        <p className="muted small">“When tapped” opens Kinmantle’s keyboard when you touch a text box, on screens without one of their own. Phones and tablets keep theirs.</p>
      </div>
      <div className="field">
        <span>Temperature</span>
        <div className="chip-row">
          {opt("temperatureUnit", "F", "°F")}
          {opt("temperatureUnit", "C", "°C")}
        </div>
      </div>
      <div className="field">
        <span>Clock</span>
        <div className="chip-row">
          {opt("clock24h", false, "12-hour")}
          {opt("clock24h", true, "24-hour")}
        </div>
      </div>
      <div className="field">
        <span>Default layout for new screens</span>
        <div className="chip-row wrap">
          {layouts.map((l) => (
            <button key={l.id} className={`chip ${settings.defaultLayoutId === l.id ? "on" : ""}`} disabled={!isAdmin} onClick={() => save({ defaultLayoutId: l.id })}>
              {l.name}
            </button>
          ))}
        </div>
        <p className="muted small">Each screen remembers the layout last chosen on it.</p>
      </div>
      <div className="field">
        <span>Reload Kinmantle</span>
        <div>
          <button className="btn" onClick={() => window.location.reload()}>
            <Icon name="refresh" size={18} /> Reload this screen
          </button>
        </div>
        <p className="muted small">Starts the app fresh, the way a browser’s reload button would. Useful on a wall screen in full-screen mode, which has no reload button.</p>
      </div>
      {error && <p className="error-text">{error}</p>}
    </div>
  );
}

const PALETTE = ["#c8693c", "#3f7f9c", "#7a9a4a", "#a65f9e", "#c99a2e", "#5b6fb8", "#b0464f", "#3d8f84"];

export function MembersSection() {
  const members = useMembers();
  const qc = useQueryClient();
  const { isAdmin } = useHousehold();
  const [editing, setEditing] = useState<Member | "new" | null>(null);
  return (
    <div className="form">
      <h3>People</h3>
      <p className="muted">
        Each person gets a color: events and chores tagged with them use it. Children get chore columns and routines. Anyone, children included, can sign in from their own phone: with a passkey (no Google account needed) or their own Google account.
      </p>
      <ul className="member-list">
        {(members.data ?? []).map((m) => (
          <li key={m.id}>
            <Avatar member={m} size="2.2rem" />
            <span>
              <strong>{m.displayName}</strong>
              <span className="muted small">
                {m.role === "owner" ? "Owner · parent" : m.role === "adult" ? "Parent / adult" : m.isChild ? "Child" : "Family member"}
                {m.canSignIn ? " · can sign in" : ""}
              </span>
            </span>
            {isAdmin && (
              <button className="btn btn-sm" onClick={() => setEditing(m)}>
                Edit
              </button>
            )}
          </li>
        ))}
      </ul>
      {isAdmin && (
        <button className="btn" onClick={() => setEditing("new")}>
          <Icon name="plus" size={18} /> Add person
        </button>
      )}
      {editing && <MemberEditor member={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => qc.invalidateQueries({ queryKey: ["members"] })} />}
    </div>
  );
}

function MemberEditor({ member, onClose, onSaved }: { member: Member | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(member?.displayName ?? "");
  const [color, setColor] = useState(member?.color ?? PALETTE[1]!);
  const [email, setEmail] = useState(member?.email ?? "");
  const [canSignIn, setCanSignIn] = useState(member?.canSignIn ?? false);
  const [role, setRole] = useState<"adult" | "child" | "member">(member?.role === "adult" ? "adult" : member?.isChild || !member ? "child" : "member");
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const isOwner = member?.role === "owner";

  const save = async () => {
    try {
      const body = isOwner
        ? { displayName: name, color }
        : role === "child"
          ? { displayName: name, color, email: email || null, canSignIn, role: "member", isChild: true }
          : { displayName: name, color, email: email || null, canSignIn, role, isChild: false };
      await api(member ? `/members/${member.id}` : "/members", { method: member ? "PATCH" : "POST", body });
      onSaved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const remove = async () => {
    try {
      await api(`/members/${member!.id}`, { method: "DELETE" });
      onSaved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Sheet
      title={member ? `Edit ${member.displayName}` : "Add person"}
      onClose={onClose}
      size="sm"
      footer={
        <>
          {member && !isOwner && (
            <button className="btn btn-danger-ghost" onClick={() => setConfirm(true)}>
              Remove
            </button>
          )}
          <span className="spacer" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={!name.trim()}>
            Save
          </button>
        </>
      }
    >
      <div className="form">
        <label className="field">
          <span>Name</span>
          <input className="input" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <div className="field">
          <span>Color</span>
          <div className="chip-row wrap">
            {PALETTE.map((c) => (
              <button key={c} className={`swatch ${color === c ? "on" : ""}`} style={{ background: c }} onClick={() => setColor(c)} aria-label={`Color ${c}`} aria-pressed={color === c} />
            ))}
          </div>
        </div>
        {!isOwner && (
          <>
            <div className="field">
              <span>Who is this?</span>
              <div className="segmented">
                <button className={role === "adult" ? "on" : ""} onClick={() => setRole("adult")}>
                  Parent / adult
                </button>
                <button className={role === "child" ? "on" : ""} onClick={() => setRole("child")}>
                  Child
                </button>
                <button className={role === "member" ? "on" : ""} onClick={() => setRole("member")}>
                  Other
                </button>
              </div>
              <p className="muted small">
                {role === "adult" ? "Can change settings, pay out rewards and manage the household." : role === "child" ? "Gets a chore column and routines; can’t change settings." : "E.g. a grandparent or sitter: shows on the calendar, can check things off."}
              </p>
            </div>
            <label className="field field-toggle">
              <span>
                Can sign in
                <small className="muted">With a passkey on their phone (below) or their own Google account. Off signs them out everywhere.</small>
              </span>
              <input type="checkbox" className="switch" checked={canSignIn} onChange={(e) => setCanSignIn(e.target.checked)} />
            </label>
            <label className="field">
              <span>Their Google account email (optional)</span>
              <input className="input" type="email" value={email} maxLength={120} onChange={(e) => setEmail(e.target.value)} placeholder="name@gmail.com" />
            </label>
          </>
        )}
        {member && <MemberSignIn member={member} onChanged={onSaved} />}
        {error && <p className="error-text">{error}</p>}
      </div>
      {confirm && (
        <Confirm title={`Remove ${member?.displayName}?`} message="Their open tasks become unassigned and they can no longer sign in." confirmLabel="Remove" danger onCancel={() => setConfirm(false)} onConfirm={remove} />
      )}
    </Sheet>
  );
}

const CONNECT_MESSAGES: Record<string, { text: string; error?: boolean }> = {
  connected: { text: "Google Calendar is connected. Now choose which calendars to show." },
  denied: { text: "Google Calendar wasn’t connected — the permission screen was cancelled.", error: true },
  scopes: { text: "Kinmantle needs permission to see your calendars. Connect again and leave both boxes ticked on Google’s screen.", error: true },
  failed: { text: "Connecting didn’t finish. Please try again.", error: true },
  forbidden: { text: "Only an adult in the household can connect Google Calendar.", error: true },
  unavailable: { text: "Calendar connection isn’t available on this Kinmantle yet.", error: true },
  demo: { text: "The demo household can’t connect a Google Calendar." },
};

const canEditRole = (role: string) => role === "owner" || role === "writer";

export function CalendarSection() {
  const status = useCalendarStatus();
  const { isAdmin } = useHousehold();
  const demo = useMe().data?.demo === true;
  const s = status.data;
  const active = s?.connection === "active";
  // One-time result of returning from Google's permission screen (?calendar=…).
  const [flash] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const key = params.get("calendar");
    if (key) {
      params.delete("calendar");
      const rest = params.toString();
      window.history.replaceState(null, "", `${window.location.pathname}${rest ? `?${rest}` : ""}${window.location.hash}`);
    }
    return key ? CONNECT_MESSAGES[key] : undefined;
  });
  const connect = (label: string) => (
    <a className="btn btn-primary" href="/api/auth/google/calendar/start">
      <Icon name="calendar" size="1.2rem" /> {label}
    </a>
  );

  return (
    <div className="form">
      <h3>Google Calendar</h3>
      <p className="muted">
        Kinmantle shows and edits your family’s Google Calendar. Google stays the source of truth — changes made here are saved to Google, and changes made on a phone appear here within about a minute.
      </p>
      {flash && <p className={`banner ${flash.error ? "banner-error" : ""}`}>{flash.text}</p>}

      {demo ? (
        <p className="banner">This is the demo, so the calendar shows sample events. Connecting a Google Calendar is turned off here.</p>
      ) : !s ? null : s.connection === "none" ? (
        !s.available ? (
          <p className="banner">Calendar connection isn’t available on this Kinmantle yet. The calendar shows sample events for now.</p>
        ) : isAdmin ? (
          <>
            <p>The calendar is showing sample events. Connect your Google account to see your real schedule.</p>
            <div className="row">{connect("Connect Google Calendar")}</div>
            <p className="muted small">
              Google will ask to let Kinmantle see your calendars and add or change events; then you choose which calendars appear. Until Google finishes reviewing Kinmantle, it may first say the app isn’t verified — choose “Advanced”, then “Go to Kinmantle”.
            </p>
          </>
        ) : (
          <p className="banner">Not connected yet — the calendar shows sample events. An adult in the household can connect it.</p>
        )
      ) : (
        <>
          {s.connection === "needs_reconnect" && (
            <div className="banner banner-error">
              <span>Google stopped accepting Kinmantle’s access (it may have been removed in the Google account). The wall is showing the last synced events.</span>
              {isAdmin && connect("Reconnect")}
            </div>
          )}
          <dl className="kv">
            {s.accountEmail && (
              <>
                <dt>Google account</dt>
                <dd>{s.accountEmail}</dd>
              </>
            )}
            <dt>Permission</dt>
            <dd>{s.canEdit ? "See and edit events" : "View only"}</dd>
            {active && s.selectedCount > 0 && (
              <>
                <dt>Last synced</dt>
                <dd>{s.lastSyncAt ? relativeAge(s.lastSyncAt) : "Not yet"}</dd>
              </>
            )}
          </dl>
          {active && !s.canEdit && isAdmin && (
            <div className="banner">
              <p>Google only granted view access, so events can’t be added or changed from the wall. Reconnect and tick the box for editing events to allow it.</p>
              {connect("Reconnect with editing")}
            </div>
          )}
          {active && isAdmin && <CalendarPicker />}
          {isAdmin && <DisconnectCalendar />}
        </>
      )}
    </div>
  );
}

function CalendarPicker() {
  const choices = useCalendarChoices(true);
  const { save } = useCalendarSetup();
  const [selected, setSelected] = useState<string[] | null>(null);
  const [defaultId, setDefaultId] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    if (!choices.data || selected !== null) return;
    setSelected(choices.data.filter((c) => c.selected).map((c) => c.id));
    setDefaultId(choices.data.find((c) => c.isDefault)?.id ?? null);
  }, [choices.data, selected]);

  if (choices.isError) return <p className="banner banner-error">Couldn’t load your calendars from Google: {choices.error.message}</p>;
  if (!choices.data || selected === null) return <p className="muted">Loading your calendars…</p>;
  const list = choices.data;
  const writable = list.filter((c) => selected.includes(c.id) && canEditRole(c.accessRole));
  const effectiveDefault = writable.some((c) => c.id === defaultId) ? defaultId : (writable[0]?.id ?? null);
  const toggle = (id: string) => {
    setMsg(null);
    setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };
  const submit = () =>
    save.mutate(
      { selected, defaultId: effectiveDefault },
      {
        onSuccess: (r) => {
          setDefaultId(r.defaultId);
          setMsg("Saved");
        },
        onError: (e) => setMsg(e.message),
      },
    );

  return (
    <div className="field">
      <span>Calendars to show</span>
      <div className="calendar-choices">
        {list.map((c) => (
          <label key={c.id} className="field field-toggle calendar-choice">
            <span>
              <span className="calendar-choice-name">
                <i className="calendar-swatch" style={{ background: c.color ?? "var(--accent)" }} />
                {c.name}
              </span>
              <small className="muted">
                {c.primary ? "Your main calendar · " : ""}
                {canEditRole(c.accessRole) ? "Can edit" : "View only"}
              </small>
            </span>
            <input type="checkbox" className="switch" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} />
          </label>
        ))}
      </div>
      {writable.length > 1 && (
        <label className="field">
          <span>New events go to</span>
          <select className="input" value={effectiveDefault ?? ""} onChange={(e) => setDefaultId(e.target.value)}>
            {writable.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="row">
        <button className="btn btn-primary" onClick={submit} disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save calendars"}
        </button>
        {msg && <span className="muted">{msg}</span>}
      </div>
    </div>
  );
}

function DisconnectCalendar() {
  const { disconnect } = useCalendarSetup();
  const [confirm, setConfirm] = useState(false);
  return (
    <>
      <hr />
      <div className="row">
        <button className="btn btn-danger-ghost" onClick={() => setConfirm(true)} disabled={disconnect.isPending}>
          {disconnect.isPending ? "Disconnecting…" : "Disconnect Google Calendar"}
        </button>
        {disconnect.isError && <span className="error-text">{disconnect.error.message}</span>}
      </div>
      {confirm && (
        <Confirm
          title="Disconnect Google Calendar?"
          message="Kinmantle will ask Google to remove its access and delete its copy of your events. Your Google Calendar itself isn’t changed. The wall goes back to sample events until you connect again."
          confirmLabel="Disconnect"
          danger
          onCancel={() => setConfirm(false)}
          onConfirm={() => {
            setConfirm(false);
            disconnect.mutate();
          }}
        />
      )}
    </>
  );
}

export function AiSection() {
  const ai = useAiStatus();
  const s = ai.data;
  return (
    <div className="form">
      <h3>AI features</h3>
      <p className="muted">
        AI is optional and <strong>off</strong>. Everything in Kinmantle works without it. When AI features arrive, each will need to be switched on individually, with spending caps you set — and every call is metered.
      </p>
      <label className="field field-toggle">
        <span>
          AI enabled
          <small className="muted">No AI provider or budget is configured, so this can’t be turned on.</small>
        </span>
        <input type="checkbox" className="switch" checked={s?.enabled ?? false} disabled readOnly />
      </label>
      <dl className="kv">
        <dt>Spent today</dt>
        <dd>${(s?.spend.todayUsd ?? 0).toFixed(2)}</dd>
        <dt>This month</dt>
        <dd>${(s?.spend.monthUsd ?? 0).toFixed(2)}</dd>
        <dt>Monthly cap</dt>
        <dd>${(s?.budgets.monthlyUsd ?? 0).toFixed(2)}</dd>
      </dl>
    </div>
  );
}

export function DiagnosticsSection() {
  const d = useDiagnostics(true);
  const [copied, setCopied] = useState(false);
  const client = {
    online: navigator.onLine,
    screen: `${window.screen.width}×${window.screen.height} @${window.devicePixelRatio}x`,
    viewport: `${window.innerWidth}×${window.innerHeight}`,
    userAgent: navigator.userAgent,
  };
  const copy = async () => {
    // Sanitized: no event text, emails, tokens or locations.
    const text = JSON.stringify({ client, server: d.data }, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="form">
      <h3>Diagnostics</h3>
      <dl className="kv">
        <dt>Build</dt>
        <dd>{d.data?.build ?? "…"}</dd>
        <dt>This screen</dt>
        <dd>
          {client.viewport} (display {client.screen}) · {client.online ? "online" : "offline"}
        </dd>
        <dt>AI</dt>
        <dd>{d.data ? (d.data.ai.enabled ? "Enabled" : "Off") : "…"}</dd>
      </dl>
      <h4>Outbound requests today (UTC)</h4>
      <table className="table">
        <thead>
          <tr>
            <th>Provider</th>
            <th>Requests</th>
            <th>Errors</th>
            <th>Rate-limited</th>
            <th>Self-imposed cap</th>
          </tr>
        </thead>
        <tbody>
          {(d.data?.usageToday ?? []).map((u) => (
            <tr key={u.provider}>
              <td>{u.provider}</td>
              <td>{u.requests}</td>
              <td>{u.errors}</td>
              <td>{u.rateLimited}</td>
              <td>{u.limit ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <h4>Data sources</h4>
      <table className="table">
        <thead>
          <tr>
            <th>Source</th>
            <th>Last success</th>
            <th>Last error</th>
          </tr>
        </thead>
        <tbody>
          {(d.data?.sources ?? []).map((s) => (
            <tr key={s.source}>
              <td>{s.source}</td>
              <td>{s.lastSuccessAt ? relativeAge(s.lastSuccessAt) : "—"}</td>
              <td>{s.lastErrorAt ? `${relativeAge(s.lastErrorAt)}: ${s.lastError}` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="btn" onClick={copy}>
        <Icon name="copy" size={18} /> {copied ? "Copied" : "Copy diagnostics"}
      </button>
    </div>
  );
}
